import api from './api';

/** Payload para cadastrar card */
export interface CreateCardPayload {
  slot: number;
  qtde_portas: number;
  model?: string;
  serialnumber?: string;
  sw?: string;
  status?: 'active' | 'inactive' | 'maintenance';
  description?: string;
}

/** Payload para editar card */
export interface UpdateCardPayload {
  model?: string;
  qtde_portas?: number;
  serialnumber?: string;
  sw?: string;
  status?: 'active' | 'inactive' | 'maintenance';
  description?: string;
}

/** Card da OLT */
export interface OLTCard {
  id: number;
  olt_id: number;
  slot: number;
  model?: string;
  qtde_portas: number;
  serialnumber?: string;
  sw?: string;
  status: 'active' | 'inactive' | 'maintenance';
  description?: string;
  last_sync?: string;
  ativo: boolean;
  olt?: {
    id: number;
    name: string;
    vendor: string;
  };
}

export interface OLT {
  id: number;
  name: string;
  vendor: 'vsolution' | 'huawei' | 'datacom' | 'fdata';
  model: string;
  ip_address: string;
  ssh_port: number;
  ssh_username: string;
  ssh_password: string;
  snmp_community: string;
  snmp_port: number;
  tipo_comunicacao: 'ssh' | 'telnet';
  porta_telnet?: number;
  status: 'online' | 'offline' | 'maintenance';
  monitor_ultimo_online_em?: string | null;
  monitor_ultimo_offline_em?: string | null;
  location?: string;
  description?: string;
  onu_profile?: string;
  ativo: boolean;
  solicita_senha_enable?: boolean;
  senha_enable?: string;
  armario_id?: number | null;
  usuarioId_criou: number;
  createdAt: string;
  updatedAt: string;
  usuario_criou?: {
    id: number;
    nome: string;
    usuario: string;
  };
  pon_ports?: PONPort[];
}

export interface PONPort {
  id: number;
  olt_id: number;
  port_number: string;
  max_onus: number;
  current_onus: number;
  status: 'active' | 'inactive' | 'maintenance';
  ativo: boolean;
  olt?: {
    id: number;
    name: string;
    vendor: string;
  };
}

export interface OLTStats {
  summary: {
    total: number;
    online: number;
    offline: number;
    maintenance: number;
  };
  byVendor: Array<{
    vendor: string;
    total: number;
  }>;
  ports: {
    total: number;
    active: number;
    inactive: number;
  };
  onus: {
    current: number;
    capacity: number;
  };
}

export interface ConnectionResult {
  success: boolean;
  message: string;
  vendor?: string;
  model?: string;
  version?: string;
  uptime?: string;
  error?: string;
  ssh?: {
    success: boolean;
    message: string;
    error?: string;
  };
  snmp?: {
    success: boolean;
    message: string;
    error?: string;
  };
}

export interface PortStatus {
  portNumber: string;
  status: 'up' | 'down' | 'maintenance';
  adminStatus: 'enabled' | 'disabled';
  operStatus: 'up' | 'down';
  onuCount: number;
  maxOnus: number;
  utilization: number;
  rxPower?: number;
  txPower?: number;
}

export interface ONUInfo {
  index: number;
  sn: string;
  mac?: string;
  status: 'online' | 'offline' | 'dying' | 'new';
  description?: string;
  distance?: number;
  rxPower?: number;
  txPower?: number;
  lastSeen?: string;
  uptime?: string;
  model?: string;
  version?: string;
  ip?: string;
}

class OLTService {
  // Listar OLTs
  async getAll(params?: {
    page?: number;
    limit?: number;
    ativo?: boolean;
    vendor?: string;
    status?: string;
    search?: string;
    armario_id?: string | null;
  }) {
    const response = await api.get('/olts', { params });
    return response.data;
  }

  // Obter estatísticas
  async getStats(): Promise<OLTStats> {
    const response = await api.get('/olts/stats');
    const backendData = response.data; // Dados estão diretamente em response.data

    if (!backendData || !backendData.summary) {
      console.error('❌ Dados OLTs inválidos:', backendData);
      throw new Error('Estrutura de dados OLTs inválida');
    }

    return backendData;
  }

  // Obter OLT específica
  async getById(id: number): Promise<{ success: boolean; data: OLT }> {
    const response = await api.get(`/olts/${id}`);
    // O backend retorna { success: true, data: {...} }
    // Mas o axios já extrai o data, então response.data já é { success: true, data: {...} }
    return response.data;
  }

  // Criar OLT
  async create(data: Partial<OLT>): Promise<{ success: boolean; data: OLT }> {
    const response = await api.post('/olts', data);
    return response.data;
  }

  // Atualizar OLT
  async update(id: number, data: Partial<OLT>): Promise<{ success: boolean; data: OLT }> {
    const response = await api.put(`/olts/${id}`, data);
    return response.data;
  }

  // Excluir OLT
  async delete(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/olts/${id}`);
    return response.data;
  }

  // Alterar status
  async toggleStatus(id: number): Promise<{ success: boolean; data: OLT }> {
    const response = await api.patch(`/olts/${id}/toggle-status`);
    return response.data;
  }

  // Testar conexão
  async testConnection(id: number): Promise<{ success: boolean; data: ConnectionResult }> {
    const response = await api.post(`/olts/${id}/test-connection`, {}, {
      timeout: 50000 // 30 segundos para teste de conexão
    });
    return response.data;
  }

  // Obter status de porta
  async getPortStatus(id: number, portNumber: string): Promise<{ success: boolean; data: PortStatus }> {
    const response = await api.get(`/olts/${id}/ports/${portNumber}/status`);
    return response.data;
  }

  // Obter ONUs de uma porta
  async getPortONUs(id: number, portNumber: string): Promise<{ success: boolean; data: ONUInfo[] }> {
    const response = await api.get(`/olts/${id}/ports/${portNumber}/onus`);
    return response.data;
  }

  // ===== CARDS =====

  /** Listar cards de uma OLT */
  async getCards(oltId: number | string): Promise<{ success: boolean; data: OLTCard[] }> {
    const response = await api.get(`/olts/${oltId}/cards`);
    return response.data;
  }

  /** Cadastrar card manualmente */
  async createCard(oltId: number | string, data: CreateCardPayload): Promise<{ success: boolean; data: OLTCard }> {
    const response = await api.post(`/olts/${oltId}/cards`, data);
    return response.data;
  }

  /** Editar card */
  async updateCard(cardId: number, data: UpdateCardPayload): Promise<{ success: boolean; data: OLTCard }> {
    const response = await api.put(`/olts/cards/${cardId}`, data);
    return response.data;
  }

  /** Deletar card */
  async deleteCard(cardId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/olts/cards/${cardId}`);
    return response.data;
  }

  /** Sincronizar cards da OLT */
  async syncCards(oltId: number | string): Promise<{
    success: boolean;
    data: { cardsCreated: number; cardsUpdated: number };
    message: string;
  }> {
    const response = await api.post(`/olts/${oltId}/cards/sync`);
    return response.data;
  }

  /** Buscar portas de um card (slot) da OLT */
  async getCardPorts(oltId: number | string, slot: number): Promise<any> {
    const response = await api.get(`/olts/${oltId}/cards/${slot}/ports`);
    return response.data;
  }
}

export const oltService = new OLTService(); 